import { useEffect, useMemo, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { BsFillPatchQuestionFill } from 'react-icons/bs';

import Modal from '../../../components/modal';
import Input from '../../../components/input';
import Button from '../../../components/button';
import SearchableSelect from '../../../components/searchableSelect';
import Select from '../../../components/select';
import Spinner from '../../../components/spinner';
import ConfirmDeleteModal from '../../../components/ConfirmDeleteModal';
import PaymentModal from '../../../components/paymentModal';

import { apiEndPoints } from '../../../hooks/remote/apiEndPoints';
import {
  listIkePolicies,
  listIpsecPolicies,
  listVpnServices,
  listVpnEndpointGroups,
  listIpsecSiteConnections,
  getUserVirtualAccount,
} from '../../../hooks/local/userReducer';
import { showSuccessMessage, showErrorMessage, setFormikErrors } from '../../../hooks/constants';
import { useRouterList, useSubnetList } from '../layout/resuableEffect';

import IkePoliciesTab from './vpnTabs/IkePoliciesTab';
import IpsecPoliciesTab from './vpnTabs/IpsecPoliciesTab';
import VpnServicesTab from './vpnTabs/VpnServicesTab';
import EndpointGroupsTab from './vpnTabs/EndpointGroupsTab';
import SiteConnectionsTab from './vpnTabs/SiteConnectionsTab';

const tabs = [
  { key: 'ike', label: 'IKE Policies' },
  { key: 'ipsec', label: 'IPSec Policies' },
  { key: 'services', label: 'VPN Services' },
  { key: 'endpointGroups', label: 'Endpoint Groups' },
  { key: 'siteConnections', label: 'IPSec Site Connections' },
];

const enumOptions = {
  auth_algorithm: ['sha1', 'sha256', 'sha384', 'sha512'].map((v) => ({ value: v, label: v })),
  encryption_algorithm: ['aes-128', 'aes-192', 'aes-256', '3des'].map((v) => ({ value: v, label: v })),
  pfs: ['group2', 'group5', 'group14'].map((v) => ({ value: v, label: v })),
  phase1_negotiation_mode: ['main', 'aggressive'].map((v) => ({ value: v, label: v })),
  // units: ['seconds', 'kilobytes'].map((v) => ({ value: v, label: v })),
  units: ['seconds'].map((v) => ({ value: v, label: v })),
  ike_version: ['v1', 'v2'].map((v) => ({ value: v, label: v })),
  encapsulation_mode: ['tunnel', 'transport'].map((v) => ({ value: v, label: v })),
  transform_protocol: ['ESP', 'AH', 'AH-ESP'].map((v) => ({ value: v, label: v })),
  endpoint_type: [
    { value: 'cidr', label: 'CIDR (for external systems)' },
    { value: 'subnet', label: 'Subnet (for local systems)' },
  ],
  initiator: ['bi-directional', 'response-only'].map((v) => ({ value: v, label: v })),
  dpd_action: ['hold', 'clear', 'restart', 'disabled'].map((v) => ({ value: v, label: v })),
};


const vpnPaymentOptions = [
  { value: '', label: 'Select payment period' },
  { value: 'monthly', label: 'Monthly' },
  { value: 'yearly', label: 'Yearly' },
];


const VPN = () => {
  document.title = 'Virtual Private Network - CloudVantage';

  const projectId = useSelector(
  (state) => state.user.selectedProject?.id,
);

const [showPaymentModal, setShowPaymentModal] = useState(false);
const [paymentVerified, setPaymentVerified] = useState(false);

const currency = useSelector((state) => state.user.currency);

const [vpnPriceData, setVpnPriceData] = useState(null);
const [vpnPriceLoading, setVpnPriceLoading] = useState(false);

useEffect(() => {
  const fetchVpnPrice = async () => {
    if (!projectId) {
      setVpnPriceData(null);
      return;
    }

    try {
      setVpnPriceLoading(true);

      const response =
        await apiEndPoints.getVpnPrice(projectId);

      const data =
        response?.data?.data ||
        response?.data ||
        response;

      console.log('VPN PRICE RESPONSE:', data);

      setVpnPriceData(data);
    } catch (error) {
      console.error(
        'Unable to fetch VPN price:',
        error
      );

      setVpnPriceData(null);
    } finally {
      setVpnPriceLoading(false);
    }
  };

  fetchVpnPrice();
}, [projectId]);

const getVpnMonthlyPrice = () => {
  if (!vpnPriceData) return null;

  const rawPrice =
    currency === "USD"
      ? (
          vpnPriceData?.dollar_price ??
          vpnPriceData?.usd_price ??
          vpnPriceData?.monthly_dollar_price
        )
      : (
          vpnPriceData?.naira_price ??
          vpnPriceData?.ngn_price ??
          vpnPriceData?.monthly_naira_price ??
          vpnPriceData?.price
        );

  if (
    rawPrice === undefined ||
    rawPrice === null ||
    rawPrice === ""
  ) {
    return null;
  }

  const parsedPrice = Number(rawPrice);

  return Number.isFinite(parsedPrice)
    ? parsedPrice
    : null;
};

const calculateVpnPrice = (paymentMethod) => {
  if (!paymentMethod) return 0;

  const monthlyPrice =
    getVpnMonthlyPrice();

  if (monthlyPrice === null) {
    return 0;
  }

  return paymentMethod === 'yearly'
    ? monthlyPrice * 12
    : monthlyPrice;
};

const virtualAccount = useSelector(
  (state) => state.user.virtualAccount
);

const walletBalance = useSelector((state) => {
  const account = state.user.virtualAccount;

  if (!account || Array.isArray(account)) {
    return null;
  }

  const rawBalance =
    currency === "USD"
      ? account?.dollar_balance
      : account?.balance;

  if (
    rawBalance === undefined ||
    rawBalance === null ||
    rawBalance === ""
  ) {
    return null;
  }

  const balance = Number(rawBalance);

  return Number.isFinite(balance)
    ? balance
    : null;
});

const dispatch = useDispatch();

const [activeTab, setActiveTab] = useState('ike');

  const [loading, setLoading] = useState(false);
  const [filterKey, setFilterKey] = useState('name');
  const [filterText, setFilterText] = useState('');
  const [selectedIds, setSelectedIds] = useState(new Set());
const [editingSiteConnection, setEditingSiteConnection] = useState(null);
const [editSiteConnectionOpen, setEditSiteConnectionOpen] = useState(false);

  // Data sets from Redux
  const {
    ikePolicies,
    ipsecPolicies,
    vpnServices,
    endpointGroups,
    siteConnections,
  } = useSelector((state) => state.user);

  const routerList = useRouterList(projectId);
  const subnetList = useSubnetList(projectId, '');

  const routerOptions = useMemo(() => {
    const arr = Array.isArray(routerList) ? routerList : [];
    return [{ value: '', label: 'Select router' }, ...arr.map((r) => ({ value: r.id, label: r.name?.trim() ? r.name : r.id }))];
  }, [routerList]);



  const endpointGroupOptions = useMemo(() => {
    const arr = Array.isArray(endpointGroups) ? endpointGroups : [];
    return [{ value: '', label: 'Select endpoint group' }, ...arr.map((g) => ({ value: g.id || g.uuid, label: g.name || g.id || g.uuid }))];
  }, [endpointGroups]);

  const localEndpointGroupOptions = useMemo(() => {
  const arr = Array.isArray(endpointGroups) ? endpointGroups : [];

  return [
    { value: '', label: 'Select local endpoint group' },
    ...arr
      .filter((g) => String(g.endpoint_type || g.type).toLowerCase() === 'subnet')
      .map((g) => ({
        value: g.id || g.uuid,
        label: g.name || g.id || g.uuid,
      })),
  ];
}, [endpointGroups]);

const peerCidREndpointGroupOptions = useMemo(() => {
  const arr = Array.isArray(endpointGroups) ? endpointGroups : [];

  return [
    { value: '', label: 'Select peer CIDR endpoint group' },
    ...arr
      .filter((g) => String(g.endpoint_type || g.type).toLowerCase() === 'cidr')
      .map((g) => ({
        value: g.id || g.uuid,
        label: g.name || g.id || g.uuid,
      })),
  ];
}, [endpointGroups]);

  const ikeOptions = useMemo(() => {
    const arr = Array.isArray(ikePolicies) ? ikePolicies : [];
    return [{ value: '', label: 'Select IKE policy' }, ...arr.map((p) => ({ value: p.id || p.uuid, label: p.name || p.id || p.uuid }))];
  }, [ikePolicies]);

  const ipsecOptions = useMemo(() => {
    const arr = Array.isArray(ipsecPolicies) ? ipsecPolicies : [];
    return [{ value: '', label: 'Select IPSec policy' }, ...arr.map((p) => ({ value: p.id || p.uuid, label: p.name || p.id || p.uuid }))];
  }, [ipsecPolicies]);

  const serviceOptions = useMemo(() => {
    const arr = Array.isArray(vpnServices) ? vpnServices : [];
    return [{ value: '', label: 'Select VPN service' }, ...arr.map((s) => ({ value: s.id || s.uuid, label: s.name || s.id || s.uuid }))];
  }, [vpnServices]);

  const refetchAll = async () => {
    if (!projectId) return;
    setLoading(true);
    try {
      await Promise.all([
        dispatch(listIkePolicies(projectId)),
        dispatch(listIpsecPolicies(projectId)),
        dispatch(listVpnServices(projectId)),
        dispatch(listVpnEndpointGroups(projectId)),
        dispatch(listIpsecSiteConnections(projectId)),
      ]);
    } catch (error) {
      console.error('Error refetching VPN data:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!projectId) return;

    setSelectedIds(new Set());

    refetchAll();

    dispatch(getUserVirtualAccount());
  }, [projectId, dispatch]);

  useEffect(() => {
    // default filter key per tab (like Horizon dropdown)
    if (activeTab === 'ike' || activeTab === 'ipsec')
      setFilterKey((k) =>
        ['name', 'auth', 'encryption', 'pfs'].includes(k) ? k : 'name',
      );
    else if (activeTab === 'services')
      setFilterKey((k) =>
        ['name', 'subnet_id', 'subnet', 'router_id', 'router'].includes(k) ? k : 'name',
      );
    else if (activeTab === 'endpointGroups')
      setFilterKey((k) => (['name', 'type', 'endpoints'].includes(k) ? k : 'name'));
    else if (activeTab === 'siteConnections')
      setFilterKey((k) =>
        [
          'name',
          'vpnservice',
          'vpnservice_id',
          'ikepolicy',
          'ikepolicy_id',
          'ipsecpolicy',
          'ipsecpolicy_id',
          'status',
        ].includes(k)
          ? k
          : 'name',
      );
    else setFilterKey('name');
  }, [activeTab]);

  useEffect(() => {
    setSelectedIds(new Set());
  }, [activeTab, filterKey, filterText]);

  const dataset = useMemo(() => {
    if (activeTab === 'ike') return ikePolicies;
    if (activeTab === 'ipsec') return ipsecPolicies;
    if (activeTab === 'services') return vpnServices;
    if (activeTab === 'endpointGroups') return endpointGroups;
    return siteConnections;
  }, [activeTab, ikePolicies, ipsecPolicies, vpnServices, endpointGroups, siteConnections]);

  const filtered = useMemo(() => {
    const arr = Array.isArray(dataset) ? dataset : [];
    const q = filterText.trim().toLowerCase();
    if (!q) return arr;
    if (filterKey === 'name') return arr.filter((x) => (x.name || '').toLowerCase().includes(q));
    if (activeTab === 'ike') {
      if (filterKey === 'auth')
        return arr.filter((x) => (x.auth_algorithm || '').toLowerCase().includes(q));
      if (filterKey === 'encryption')
        return arr.filter((x) =>
          (x.encryption_algorithm || '').toLowerCase().includes(q),
        );
      if (filterKey === 'pfs')
        return arr.filter((x) => (x.pfs || '').toLowerCase().includes(q));
    }
    if (activeTab === 'ipsec') {
      if (filterKey === 'auth')
        return arr.filter((x) => (x.auth_algorithm || '').toLowerCase().includes(q));
      if (filterKey === 'encryption')
        return arr.filter((x) =>
          (x.encryption_algorithm || '').toLowerCase().includes(q),
        );
      if (filterKey === 'pfs')
        return arr.filter((x) => (x.pfs || '').toLowerCase().includes(q));
    }
    if (activeTab === 'services') {
      if (filterKey === 'subnet_id')
        return arr.filter((x) => (x.subnet_id || '').toLowerCase().includes(q));
      if (filterKey === 'subnet')
        return arr.filter((x) => {
          const s = subnetList?.find?.((n) => n.id === x.subnet_id);
          const label = s?.name ? `${s.name} (${s.cidr})` : s?.cidr || '';
          return label.toLowerCase().includes(q);
        });
      if (filterKey === 'router_id')
        return arr.filter((x) => (x.router_id || '').toLowerCase().includes(q));
      if (filterKey === 'router')
        return arr.filter((x) => {
          const r = routerList?.find?.((n) => n.id === x.router_id);
          const label = r?.name || '';
          return label.toLowerCase().includes(q);
        });
    }
    if (activeTab === 'endpointGroups') {
      if (filterKey === 'type')
        return arr.filter((x) => (x.endpoint_type || '').toLowerCase().includes(q));
      if (filterKey === 'endpoints')
        return arr.filter((x) => {
          const eps = Array.isArray(x.endpoints) ? x.endpoints.join(', ') : x.endpoints || '';
          return String(eps).toLowerCase().includes(q);
        });
    }
    if (activeTab === 'siteConnections') {
      const byIdOrName = (list, id) => {
        const found = Array.isArray(list) ? list.find((x) => (x.id || x.uuid) === id) : null;
        return found?.name || '';
      };
      if (filterKey === 'vpnservice_id')
        return arr.filter((x) => (x.vpnservice_id || '').toLowerCase().includes(q));
      if (filterKey === 'vpnservice')
        return arr.filter((x) => byIdOrName(vpnServices, x.vpnservice_id).toLowerCase().includes(q));
      if (filterKey === 'ikepolicy_id')
        return arr.filter((x) => (x.ikepolicy_id || '').toLowerCase().includes(q));
      if (filterKey === 'ikepolicy')
        return arr.filter((x) => byIdOrName(ikePolicies, x.ikepolicy_id).toLowerCase().includes(q));
      if (filterKey === 'ipsecpolicy_id')
        return arr.filter((x) => (x.ipsecpolicy_id || '').toLowerCase().includes(q));
      if (filterKey === 'ipsecpolicy')
        return arr.filter((x) => byIdOrName(ipsecPolicies, x.ipsecpolicy_id).toLowerCase().includes(q));
      if (filterKey === 'status')
        return arr.filter((x) => (x.status || '').toLowerCase().includes(q));
    }
    return arr;
  }, [dataset, filterKey, filterText, activeTab, routerList, subnetList, vpnServices, ikePolicies, ipsecPolicies]);

  // Bulk selection helpers
  const toggleAll = () => {
    const next = new Set(selectedIds);
    const ids = filtered.map((x) => x.id || x.uuid).filter(Boolean);
    const allSelected = ids.length > 0 && ids.every((id) => next.has(id));
    if (allSelected) ids.forEach((id) => next.delete(id));
    else ids.forEach((id) => next.add(id));
    setSelectedIds(next);
  };
  const toggleOne = (id) => {
    const next = new Set(selectedIds);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setSelectedIds(next);
  };

  const [createOpen, setCreateOpen] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState({ open: false, id: '', name: '', bulk: false });
  const [siteFormStep, setSiteFormStep] = useState('required');

  const openCreate = () => {
    setEditingSiteConnection(null);
    setEditSiteConnectionOpen(false);
    setSiteFormStep('required');

    setPaymentVerified(false);
    setShowPaymentModal(false);

    siteConnectionForm.resetForm({
      values: siteConnectionForm.initialValues,
    });

    setCreateOpen(true);
  };


  const openBulkDelete = () => {
  if (selectedIds.size === 0) return;

  setConfirmDelete({
    open: true,
    id: 'bulk',
    name: 'Delete',
    bulk: true,
  });
};

  const runDelete = async () => {
    if (!projectId) return;
    const ids = confirmDelete.bulk ? [...selectedIds] : [confirmDelete.id];
    if (!ids.length) return;
    setLoading(true);
    try {
      for (const id of ids) {
        if (activeTab === 'ike') await apiEndPoints.deleteIkePolicy(projectId, id);
        else if (activeTab === 'ipsec') await apiEndPoints.deleteIpsecPolicy(projectId, id);
        else if (activeTab === 'services') await apiEndPoints.deleteVpnService(projectId, id);
        else if (activeTab === 'endpointGroups') await apiEndPoints.deleteVpnEndpointGroup(projectId, id);
        else await apiEndPoints.deleteIpsecSiteConnection(projectId, id);
      }
      showSuccessMessage('Deleted');
      setConfirmDelete({ open: false, id: '', name: '', bulk: false });
      setSelectedIds(new Set());
      await refetchAll();
    } finally {
      setLoading(false);
    }
  };

  // --- Forms (Create only, per tab) ---
  const ikeForm = useFormik({
    initialValues: {
      name: '',
      description: '',
      auth_algorithm: 'sha1',
      encryption_algorithm: 'aes-128',
      ike_version: 'v1',
      pfs: 'group5',
      phase1_negotiation_mode: 'main',
      units: 'seconds',
      value: 3600,
    },
    validationSchema: Yup.object({
      name: Yup.string().required('Name is required'),
      auth_algorithm: Yup.string().required('Auth algorithm is required'),
      encryption_algorithm: Yup.string().required('Encryption algorithm is required'),
      ike_version: Yup.string().required('IKE version is required'),
      pfs: Yup.string().required('PFS is required'),
    }),
    onSubmit: async (values, { resetForm }) => {
      if (!projectId) return;
      setLoading(true);
      try {
        const payload = { ...values };
        if (!payload.description?.trim()) {
          delete payload.description;
        }
        await apiEndPoints.createIkePolicy(projectId, payload);
        showSuccessMessage('IKE Policy created');
        setCreateOpen(false);
        resetForm();
        await refetchAll();
      } catch (error) {
        setFormikErrors(error, ikeForm.setErrors);
      } finally {
        setLoading(false);
      }
    },
  });

  const ipsecForm = useFormik({
    initialValues: {
      name: '',
      description: '',
      auth_algorithm: 'sha1',
      encryption_algorithm: 'aes-128',
      encapsulation_mode: 'tunnel',
      transform_protocol: 'ESP',
      pfs: 'group5',
      units: 'seconds',
      value: 3600,
    },
    validationSchema: Yup.object({
      name: Yup.string().required('Name is required'),
      auth_algorithm: Yup.string().required('Auth algorithm is required'),
      encryption_algorithm: Yup.string().required('Encryption algorithm is required'),
      pfs: Yup.string().required('PFS is required'),
      transform_protocol: Yup.string().required('Transform protocol is required'),
      encapsulation_mode: Yup.string().required('Encapsulation mode is required'),
    }),
    onSubmit: async (values, { resetForm }) => {
      if (!projectId) return;
      setLoading(true);
      try {
        const payload = { ...values };
        if (!payload.description?.trim()) {
          delete payload.description;
        }
        await apiEndPoints.createIpsecPolicy(projectId, payload);
        showSuccessMessage('IPSec Policy created');
        setCreateOpen(false);
        resetForm();
        await refetchAll();
      } catch (error) {
        setFormikErrors(error, ipsecForm.setErrors);
      } finally {
        setLoading(false);
      }
    },
  });

  const serviceForm = useFormik({
    initialValues: { name: '', description: '', router_id: '', admin_state_up: true },
    validationSchema: Yup.object({
      name: Yup.string().required('Name is required'),
      router_id: Yup.string().required('Router is required'),
    }),
    onSubmit: async (values, { resetForm }) => {
      if (!projectId) return;
      setLoading(true);
      try {
        const payload = { ...values };
        if (!payload.description?.trim()) {
          delete payload.description;
        }
        await apiEndPoints.createVpnService(projectId, payload);
        showSuccessMessage('VPN Service created');
        setCreateOpen(false);
        resetForm();
        await refetchAll();
      } catch (error) {
        setFormikErrors(error, serviceForm.setErrors);
      } finally {
        setLoading(false);
      }
    },
  });

  const endpointGroupForm = useFormik({
    initialValues: {
      name: '',
      description: '',
      endpoint_type: 'cidr',
      endpointsText: '',
      subnetIds: [],
    },
    validationSchema: Yup.object({
      name: Yup.string().required('Name is required'),
      endpoint_type: Yup.string().required('Endpoint type is required'),
      endpointsText: Yup.string().when('endpoint_type', {
        is: 'cidr',
        then: (schema) => schema.required('Endpoints is required'),
      }),
      subnetIds: Yup.array().when('endpoint_type', {
        is: 'subnet',
        then: (schema) => schema.min(1, 'Please select at least one subnet'),
      }),
    }),
    onSubmit: async (values, { resetForm }) => {
      if (!projectId) return;

      let endpoints = [];

      if (values.endpoint_type === 'cidr') {
        endpoints = values.endpointsText
          .split(',')
          .map((s) => s.trim())
          .filter(Boolean);
      } else {
        endpoints = values.subnetIds;
      }

      if (!endpoints.length) {
        showErrorMessage('Please provide at least one endpoint');
        return;
      }

      setLoading(true);

      try {
        const payload = {
          name: values.name,
          endpoint_type: values.endpoint_type,
          endpoints,
        };

        if (values.description?.trim()) {
          payload.description = values.description;
        }

        const response =
          await apiEndPoints.createVpnEndpointGroup(
            projectId,
            payload,
          );

        const data =
          response?.data ||
          response;

        // Backend returned an application-level error
        if (
          data?.statuscode &&
          data.statuscode !== '00'
        ) {
          showErrorMessage(
            data?.statusmessage ||
              data?.message ||
              'Unable to create endpoint group',
          );

          return;
        }

        showSuccessMessage(
          data?.statusmessage ||
            'Endpoint Group created',
        );

        setCreateOpen(false);
        resetForm();

        await refetchAll();
      } catch (error) {
        const errorData =
          error?.response?.data ||
          error?.data ||
          error;

        // Show the actual backend message to the user
        showErrorMessage(
          errorData?.statusmessage ||
            errorData?.message ||
            error?.message ||
            'Unable to create endpoint group',
        );

        // Keep this too, in case backend returns field-level validation errors
        setFormikErrors(
          error,
          endpointGroupForm.setErrors,
        );
      } finally {
        setLoading(false);
      }
    },
  });

  const validateSiteRequiredStep = (values) => {
    const errors = {};
    const req = (k, msg) => {
      if (!String(values?.[k] ?? '').trim()) errors[k] = msg;
    };
    req('name', 'Name is required');
    req('vpnservice_id', 'VPN Service is required');
    req('ikepolicy_id', 'IKE Policy is required');
    req('ipsecpolicy_id', 'IPSec Policy is required');
    req('peer_address', 'Peer address is required');
    req('peer_id', 'Peer ID is required');
    req('psk', 'PSK is required');
    if (String(values?.psk ?? '').length > 0 && String(values?.psk ?? '').length < 8) errors.psk = 'PSK should be at least 8 characters';

    req('local_ep_group_id', 'Local Endpoint Group is required');
    req('peer_ep_group_id', 'Peer Endpoint Group is required');

    return errors;
  };

  const touchFields = (formik, fields) => {
    const next = { ...(formik.touched || {}) };
    fields.forEach((f) => {
      next[f] = true;
    });
    formik.setTouched(next, true);
  };

  const siteConnectionForm = useFormik({
    initialValues: {
      name: '',
      description: '',
      vpnservice_id: '',
      ikepolicy_id: '',
      ipsecpolicy_id: '',
      peer_address: '',
      peer_id: '',
      psk: '',
      mtu: 1500,
      initiator: 'bi-directional',
      admin_state_up: true,
      action: 'restart',
      interval: 30,
      timeout: 120,
      local_id: '',
      local_ep_group_id: '',
      peer_ep_group_id: '',
      payment: '',
    },
    validationSchema: Yup.object({
      name: Yup.string().required('Name is required'),
      vpnservice_id: Yup.string().required('VPN Service is required'),
      ikepolicy_id: Yup.string().required('IKE Policy is required'),
      ipsecpolicy_id: Yup.string().required('IPSec Policy is required'),
      peer_address: Yup.string().required('Peer address is required'),
      peer_id: Yup.string().required('Peer ID is required'),
      psk: Yup.string().required('PSK is required').min(8, 'PSK should be at least 8 characters'),
      local_ep_group_id: Yup.string().required('Local Endpoint Group is required'),
      peer_ep_group_id: Yup.string().required('Peer Endpoint Group is required'),
      payment: Yup.string().when([], {
      is: () => !editingSiteConnection,
      then: (schema) =>
        schema.required('Payment period is required'),
      otherwise: (schema) => schema.notRequired(),
    }),
    }),
 onSubmit: async (values, { resetForm }) => {
  if (!projectId) return;

  const requiredErrors =
    validateSiteRequiredStep(values);

  if (Object.keys(requiredErrors).length) {
    siteConnectionForm.setErrors(requiredErrors);

    touchFields(
      siteConnectionForm,
      Object.keys(requiredErrors)
    );

    setSiteFormStep('required');

    showErrorMessage(
      'Please complete all required fields'
    );

    return;
  }

  // Payment is required only for CREATE
  if (
    !editingSiteConnection &&
    !values.payment
  ) {
    siteConnectionForm.setFieldError(
      'payment',
      'Payment period is required'
    );

    siteConnectionForm.setFieldTouched(
      'payment',
      true,
      false
    );

    setSiteFormStep('optional');

    showErrorMessage(
      'Please select a payment period'
    );

    return;
  }

  // Ensure backend VPN price is available
  if (
    !editingSiteConnection &&
    getVpnMonthlyPrice() === null
  ) {
    showErrorMessage(
      'Unable to retrieve the VPN price for this zone. Please try again.'
    );

    return;
  }

  const vpnPrice =
    calculateVpnPrice(values.payment);

    console.log("===== VPN PAYMENT DEBUG =====");

console.log("Project ID:", projectId);

console.log("Currency:", currency);

console.log("VPN Price Data:", vpnPriceData);

console.log("Monthly Price:", getVpnMonthlyPrice());

console.log("Selected Payment:", values.payment);

console.log("Calculated VPN Price:", vpnPrice);

console.log("Wallet Balance:", walletBalance);

console.log("Virtual Account:", virtualAccount);

console.log(
  "Account Number:",
  virtualAccount?.acct_num
);

console.log(
  "Has Permanent Account:",
  Boolean(
    String(virtualAccount?.acct_num || "").trim()
  )
);

console.log("Payment Verified:", paymentVerified);

console.log("=============================");
  // If wallet is insufficient, open payment modal
  if (
  !editingSiteConnection &&
  walletBalance === null
) {
  showErrorMessage(
    "Wallet information is still loading. Please try again."
  );
  return;
}
  
  if (
    !editingSiteConnection &&
    !paymentVerified &&
    vpnPrice > walletBalance
  ) {
    // Hide the IPSec form modal so PaymentModal appears on top
    setCreateOpen(false);

    setShowPaymentModal(true);

    return;
  }

  const payload = {
    name: values.name,
    vpnservice_id: values.vpnservice_id,
    ikepolicy_id: values.ikepolicy_id,
    ipsecpolicy_id: values.ipsecpolicy_id,
    peer_address: values.peer_address,
    peer_id: values.peer_id,
    psk: values.psk,
    mtu: Number(values.mtu || 1500),
    initiator:
      values.initiator || 'bi-directional',
    admin_state_up:
      !!values.admin_state_up,
    local_ep_group_id:
      values.local_ep_group_id,
    peer_ep_group_id:
      values.peer_ep_group_id,
    action:
      values.action || 'restart',
    interval:
      Number(values.interval || 30),
    timeout:
      Number(values.timeout || 120),
  };

  if (!editingSiteConnection) {
    payload.payment_method =
      values.payment;
  }

  if (values.description?.trim()) {
    payload.description =
      values.description.trim();
  }

  if (values.local_id?.trim()) {
    payload.local_id =
      values.local_id.trim();
  }

  setLoading(true);

  try {
    let response;

    if (editingSiteConnection) {
      response =
        await apiEndPoints.updateIpsecSiteConnection(
          projectId,
          editingSiteConnection.id ||
            editingSiteConnection.uuid,
          {
            ipsec_site_connection: payload,
          }
        );
    } else {
     response =
  await apiEndPoints.createIpsecSiteConnection(
    projectId,
    payload
  );
    }

    const data =
      response?.data?.data ||
      response?.data ||
      response;

   if (
  data?.statuscode &&
  data.statuscode !== '00'
) {
  let errorMessage =
    data?.statusmessage ||
    data?.message ||
    'Unable to process IPSec Site Connection';

  // Give a clearer message for subnet/router mismatch
  if (data?.type === 'SubnetIsNotConnectedToRouter') {
    errorMessage =
      'The subnet in the selected Local Endpoint Group is not connected to the router used by the selected VPN Service. Please connect the subnet to the router or select a compatible endpoint group.';
  }

  showErrorMessage(errorMessage);
  return;
}


    if (editingSiteConnection) {
      showSuccessMessage(
        'IPSec Site Connection updated'
      );

      setEditSiteConnectionOpen(false);
      setEditingSiteConnection(null);
    } else {
      showSuccessMessage(
        'IPSec Site Connection created'
      );

      setPaymentVerified(false);
      setShowPaymentModal(false);
      setCreateOpen(false);
    }

    resetForm();
    await refetchAll();

  } catch (error) {
    const errorData =
      error?.response?.data ||
      error?.data ||
      error;

   let errorMessage =
  errorData?.statusmessage ||
  errorData?.message ||
  error?.message ||
  'Unable to process IPSec Site Connection';
if (errorData?.type === 'SubnetIsNotConnectedToRouter') {
  errorMessage =
    'The subnet in the selected Local Endpoint Group is not connected to the router used by the selected VPN Service. Please connect the subnet to the router or select a compatible endpoint group.';
}

showErrorMessage(errorMessage);


    setFormikErrors(
      error,
      siteConnectionForm.setErrors
    );

  } finally {
    setLoading(false);
  }
},
});

  const createTitle =
    activeTab === 'ike'
      ? 'Add IKE Policy'
      : activeTab === 'ipsec'
        ? 'Add IPSec Policy'
        : activeTab === 'services'
          ? 'Add VPN Service'
          : activeTab === 'endpointGroups'
            ? 'Add Endpoint Group'
            : 'Add IPSec Site Connection';

  const openEditSiteConnection = (row) => {
  setEditingSiteConnection(row);
  setEditSiteConnectionOpen(true);
  setCreateOpen(false);
  setSiteFormStep('required');

  siteConnectionForm.setValues({
    name: row.name || '',
    description: row.description || '',
    vpnservice_id: row.vpnservice_id || '',
    ikepolicy_id: row.ikepolicy_id || '',
    ipsecpolicy_id: row.ipsecpolicy_id || '',
    peer_address: row.peer_address || '',
    peer_id: row.peer_id || '',
    psk: '',
    mtu: row.mtu || 1500,
    initiator: row.initiator || 'bi-directional',
    admin_state_up: row.admin_state_up ?? true,
    action: row.dpd?.action || 'restart',
interval: row.dpd?.interval || 30,
timeout: row.dpd?.timeout || 120,
    local_id: row.local_id || '',
    local_ep_group_id: row.local_ep_group_id || '',
    peer_ep_group_id: row.peer_ep_group_id || '',
  });
};

const isIkePolicyInUse = (policyId) => {
  return Array.isArray(siteConnections)
    ? siteConnections.some((conn) => conn.ikepolicy_id === policyId)
    : false;
};

const isIpsecPolicyInUse = (policyId) => {
  return Array.isArray(siteConnections)
    ? siteConnections.some((conn) => conn.ipsecpolicy_id === policyId)
    : false;
};

  const renderTable = () => {
    const onRequestDelete = (id, name, title) =>
      setConfirmDelete({ open: true, id, name, title: title || 'Delete item', bulk: false });

    if (activeTab === 'ike')
      return (
        <IkePoliciesTab
          filtered={filtered}
          selectedIds={selectedIds}
          toggleAll={toggleAll}
          toggleOne={toggleOne}
          onOpenCreate={openCreate}
          onRefresh={() => refetchAll()}
          onRequestDelete={onRequestDelete}
          projectId={projectId}
          apiEndPoints={apiEndPoints}
          setLoading={setLoading}
          refetchAll={refetchAll}
          enumOptions={enumOptions}
          isPolicyInUse={isIkePolicyInUse}
        />
      );

    if (activeTab === 'ipsec')
      return (
        <IpsecPoliciesTab
          filtered={filtered}
          selectedIds={selectedIds}
          toggleAll={toggleAll}
          toggleOne={toggleOne}
          onOpenCreate={openCreate}
          onRefresh={() => refetchAll()}
          onRequestDelete={onRequestDelete}
          projectId={projectId}
          apiEndPoints={apiEndPoints}
          setLoading={setLoading}
          refetchAll={refetchAll}
          enumOptions={enumOptions}
          isPolicyInUse={isIpsecPolicyInUse}
        />
      );

    if (activeTab === 'services')
      return (
        <VpnServicesTab
          filtered={filtered}
          selectedIds={selectedIds}
          toggleAll={toggleAll}
          toggleOne={toggleOne}
          onOpenCreate={openCreate}
          onRefresh={() => refetchAll()}
          onRequestDelete={onRequestDelete}
          projectId={projectId}
          apiEndPoints={apiEndPoints}
          setLoading={setLoading}
          refetchAll={refetchAll}
          routerList={routerList}
          subnetList={subnetList}
        />
      );

    if (activeTab === 'endpointGroups')
      return (
        <EndpointGroupsTab
          filtered={filtered}
          selectedIds={selectedIds}
          toggleAll={toggleAll}
          toggleOne={toggleOne}
          onOpenCreate={openCreate}
          onRefresh={() => refetchAll()}
          onRequestDelete={onRequestDelete}
          projectId={projectId}
          apiEndPoints={apiEndPoints}
          setLoading={setLoading}
          refetchAll={refetchAll}
          enumOptions={enumOptions}
          subnetList={subnetList}
        />
      );

    return (
     <SiteConnectionsTab
      filtered={filtered}
      selectedIds={selectedIds}
      toggleAll={toggleAll}
      toggleOne={toggleOne}
      onOpenCreate={openCreate}
      onRefresh={() => refetchAll()}
      onRequestDelete={onRequestDelete}
      onOpenEdit={openEditSiteConnection}
      projectId={projectId}
      apiEndPoints={apiEndPoints}
      setLoading={setLoading}
      refetchAll={refetchAll}
      vpnServices={vpnServices}
      ikePolicies={ikePolicies}
      ipsecPolicies={ipsecPolicies}
      endpointGroupOptions={endpointGroupOptions}
      serviceOptions={serviceOptions}
      ikeOptions={ikeOptions}
      ipsecOptions={ipsecOptions}
      enumOptions={enumOptions}
    />
    );
  };

 const f = siteConnectionForm;

const setSiteField = (field) => (e) => {
  const value = e?.target?.value || '';

  f.setFieldValue(field, value);
  f.setFieldTouched(field, true, false);

  if (f.errors[field]) {
    f.setErrors({
      ...f.errors,
      [field]: undefined,
    });
  }
};

const siteError = (field) => {
  const value = f.values[field];

  if (value !== undefined && value !== null && String(value).trim() !== '') {
    return null;
  }

  return f.touched[field] ? f.errors[field] : null;
};

//   const handleSiteSelectChange = (field) => (e) => {
//   f.setFieldValue(field, e.target.value);
//   f.setFieldTouched(field, true, false);

//   if (f.errors[field]) {
//     f.setErrors({
//       ...f.errors,
//       [field]: undefined,
//     });
//   }
// };

  const renderCreateForm = () => {
    if (activeTab === 'ike') {
      return (
        <form className="grid gap-4" onSubmit={ikeForm.handleSubmit}>
          <div className="rounded-md border border-blue-200 bg-blue-50 p-4 dark:border-blue-900 dark:bg-blue-900/20">
          <p className="text-sm font-semibold text-blue-700 dark:text-blue-300">
            VPN Service Pricing
          </p>

         <p className="mt-1 text-xs text-blue-600 dark:text-blue-400">
          {vpnPriceLoading
            ? 'Loading VPN pricing...'
            : getVpnMonthlyPrice() !== null
              ? `VPN service costs ${
                  currency === 'NGN' ? '₦' : '$'
                }${getVpnMonthlyPrice().toLocaleString()} monthly. Payment will be required when you complete the IPSec Site Connection setup.`
              : 'VPN pricing is currently unavailable.'}
        </p>
        </div>
          <Input
            label="Name *"
            name="name"
            value={ikeForm.values.name}
            onChange={ikeForm.handleChange}
            onBlur={ikeForm.handleBlur}
            onError={ikeForm.touched.name ? ikeForm.errors.name : null}
            isLabelIcon={true}
            iconTitle="The name of the IKE policy."
          />
          <Input
            label="Description"
            name="description"
            value={ikeForm.values.description}
            onChange={ikeForm.handleChange}
            onBlur={ikeForm.handleBlur}
            isLabelIcon={true}
            iconTitle="A description for the IKE policy."
          />
          <SearchableSelect
            label="Auth Algorithm *"
            name="auth_algorithm"
            value={ikeForm.values.auth_algorithm}
            onChange={ikeForm.handleChange}
            onBlur={ikeForm.handleBlur}
            options={enumOptions.auth_algorithm}
            onError={ikeForm.touched.auth_algorithm ? ikeForm.errors.auth_algorithm : null}
            isLabelIcon={true}
            iconTitle="Valid algorithms are sha1, sha256, sha384 and sha512."
          />
          <SearchableSelect
            label="Encryption Algorithm *"
            name="encryption_algorithm"
            value={ikeForm.values.encryption_algorithm}
            onChange={ikeForm.handleChange}
            onBlur={ikeForm.handleBlur}
            options={enumOptions.encryption_algorithm}
            onError={ikeForm.touched.encryption_algorithm ? ikeForm.errors.encryption_algorithm : null}
            isLabelIcon={true}
            iconTitle="Valid algorithms are 3des, aes-128, aes-192 and aes-256."
          />
          <SearchableSelect
            label="IKE Version *"
            name="ike_version"
            value={ikeForm.values.ike_version}
            onChange={ikeForm.handleChange}
            onBlur={ikeForm.handleBlur}
            options={enumOptions.ike_version}
            onError={ikeForm.touched.ike_version ? ikeForm.errors.ike_version : null}
            isLabelIcon={true}
            iconTitle="The type of version (v1/v2) that needs to be filtered."
          />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <SearchableSelect
              label="PFS *"
              name="pfs"
              value={ikeForm.values.pfs}
              onChange={ikeForm.handleChange}
              onBlur={ikeForm.handleBlur}
              options={enumOptions.pfs}
              onError={ikeForm.touched.pfs ? ikeForm.errors.pfs : null}
              isLabelIcon={true}
              iconTitle="PFS limited to using Diffie-Hellman groups 2, 5 (default) and 14."
            />
            <SearchableSelect
              label="Phase 1 Mode *"
              name="phase1_negotiation_mode"
              value={ikeForm.values.phase1_negotiation_mode}
              onChange={ikeForm.handleChange}
              onBlur={ikeForm.handleBlur}
              options={enumOptions.phase1_negotiation_mode}
              onError={ikeForm.touched.phase1_negotiation_mode ? ikeForm.errors.phase1_negotiation_mode : null}
              isLabelIcon={true}
              iconTitle="Phase 1 negotiation mode limited to using 'main' and 'aggressive'."
            />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <SearchableSelect
              label="Lifetime Units *"
              name="units"
              value={ikeForm.values.units}
              onChange={ikeForm.handleChange}
              onBlur={ikeForm.handleBlur}
              options={enumOptions.units}
              onError={ikeForm.touched.units ? ikeForm.errors.units : null}
              isLabelIcon={true}
              iconTitle="Life time consists of units and value. Units in 'seconds' and the default value is 3600."
            />
            <Input
              label="Lifetime Value *"
              name="value"
              type="number"
              value={ikeForm.values.value}
              onChange={ikeForm.handleChange}
              onBlur={ikeForm.handleBlur}
              onError={ikeForm.touched.value ? ikeForm.errors.value : null}
              isLabelIcon={true}
              iconTitle="Life time consists of units and value. Units in 'seconds' and the default value is 3600."
            />
          </div>
          <div className="flex justify-end gap-2">
            <Button buttonText="Create" buttonRole="submit" />
          </div>
        </form>
      );
    }
    if (activeTab === 'ipsec') {
      return (
        <form className="grid gap-4" onSubmit={ipsecForm.handleSubmit}>
          <Input
            label="Name *"
            name="name"
            value={ipsecForm.values.name}
            onChange={ipsecForm.handleChange}
            onBlur={ipsecForm.handleBlur}
            onError={ipsecForm.touched.name ? ipsecForm.errors.name : null}
            isLabelIcon={true}
            iconTitle="The name of the IPsec policy."
          />
          <Input
            label="Description"
            name="description"
            value={ipsecForm.values.description}
            onChange={ipsecForm.handleChange}
            onBlur={ipsecForm.handleBlur}
            onError={ipsecForm.touched.description ? ipsecForm.errors.description : null}
            isLabelIcon={true}
            iconTitle="A description for the IPsec policy."
          />
          <SearchableSelect
            label="Auth Algorithm *"
            name="auth_algorithm"
            value={ipsecForm.values.auth_algorithm}
            onChange={ipsecForm.handleChange}
            onBlur={ipsecForm.handleBlur}
            options={enumOptions.auth_algorithm}
            onError={ipsecForm.touched.auth_algorithm ? ipsecForm.errors.auth_algorithm : null}
            isLabelIcon={true}
            iconTitle="Valid algorithms are sha1, sha256, sha384 and sha512."
          />
          <SearchableSelect
            label="Encryption Algorithm *"
            name="encryption_algorithm"
            value={ipsecForm.values.encryption_algorithm}
            onChange={ipsecForm.handleChange}
            onBlur={ipsecForm.handleBlur}
            options={enumOptions.encryption_algorithm}
            onError={ipsecForm.touched.encryption_algorithm ? ipsecForm.errors.encryption_algorithm : null}
            isLabelIcon={true}
            iconTitle="Valid algorithms are 3des, aes-128, aes-192 and aes-256."
          />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <SearchableSelect
              label="Encapsulation Mode *"
              name="encapsulation_mode"
              value={ipsecForm.values.encapsulation_mode}
              onChange={ipsecForm.handleChange}
              onBlur={ipsecForm.handleBlur}
              options={enumOptions.encapsulation_mode}
              onError={ipsecForm.touched.encapsulation_mode ? ipsecForm.errors.encapsulation_mode : null}
              isLabelIcon={true}
              iconTitle="The type of IPsec tunnel (tunnel/transport) to be used."
            />
            <SearchableSelect
              label="Transform Protocol *"
              name="transform_protocol"
              value={ipsecForm.values.transform_protocol}
              onChange={ipsecForm.handleChange}
              onBlur={ipsecForm.handleBlur}
              options={enumOptions.transform_protocol}
              onError={ipsecForm.touched.transform_protocol ? ipsecForm.errors.transform_protocol : null}
              isLabelIcon={true}
              iconTitle="The type of protocol (esp, ah, ah-esp) used in IPsec policy."
            />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <SearchableSelect
              label="PFS *"
              name="pfs"
              value={ipsecForm.values.pfs}
              onChange={ipsecForm.handleChange}
              onBlur={ipsecForm.handleBlur}
              options={enumOptions.pfs}
              onError={ipsecForm.touched.pfs ? ipsecForm.errors.pfs : null}
              isLabelIcon={true}
              iconTitle="PFS limited to using Diffie-Hellman groups 2, 5 (default) and 14."
            />
            <SearchableSelect
              label="Lifetime Units *"
              name="units"
              value={ipsecForm.values.units}
              onChange={ipsecForm.handleChange}
              onBlur={ipsecForm.handleBlur}
              options={enumOptions.units}
              onError={ipsecForm.touched.units ? ipsecForm.errors.units : null}
              isLabelIcon={true}
              iconTitle="Life time consists of units and value. Units in 'seconds' and the default value is 3600."
            />
          </div>
          <Input
            label="Lifetime Value *"
            name="value"
            type="number"
            value={ipsecForm.values.value}
            onChange={ipsecForm.handleChange}
            onBlur={ipsecForm.handleBlur}
            onError={ipsecForm.touched.value ? ipsecForm.errors.value : null}
            isLabelIcon={true}
            iconTitle="Life time consists of units and value. Units in 'seconds' and the default value is 3600."
          />
          <div className="flex justify-end gap-2">
            <Button buttonText="Create" buttonRole="submit" />
          </div>
        </form>
      );
    }
    if (activeTab === 'services') {
      return (
        <form className="grid gap-4" onSubmit={serviceForm.handleSubmit}>
          <Input
            label="Name *"
            name="name"
            value={serviceForm.values.name}
            onChange={serviceForm.handleChange}
            onBlur={serviceForm.handleBlur}
            onError={serviceForm.touched.name ? serviceForm.errors.name : null}
            isLabelIcon={true}
            iconTitle="The name of the VPN service."
          />
          <Input
            label="Description"
            name="description"
            value={serviceForm.values.description}
            onChange={serviceForm.handleChange}
            onBlur={serviceForm.handleBlur}
            isLabelIcon={true}
            iconTitle="A description for the VPN service."
          />
          <div className="relative z-[9999] overflow-visible">
          <SearchableSelect
          label="Router *"
          name="router_id"
          value={serviceForm.values.router_id}
          onChange={(e) => {
            serviceForm.setFieldValue('router_id', e.target.value);
            serviceForm.setFieldTouched('router_id', true, false);

            if (serviceForm.errors.router_id) {
              serviceForm.setErrors({
                ...serviceForm.errors,
                router_id: undefined,
              });
            }
          }}
          onBlur={() => serviceForm.setFieldTouched('router_id', true)}
          options={routerOptions}
          placeholder="router name"
          onError={
            serviceForm.touched.router_id &&
            serviceForm.errors.router_id &&
            !serviceForm.values.router_id
              ? serviceForm.errors.router_id
              : null
          }
          isLabelIcon={true}
          iconTitle="The router and admin state fields require to be enabled."
        />
</div>
          <div className="flex items-center gap-2">
             <input
            type="checkbox"
            name="admin_state_up"
            checked={true}
            readOnly
            disabled
          />
            <span className="text-sm flex items-center">
              Admin State Up
              <BsFillPatchQuestionFill
                className="text-sm text-primaryBlue ml-1 cursor-pointer"
                title="The state of VPN service to start in. if disabled (not checked), VPN service does not forward packets."
              />
            </span>
          </div>
          <div className="flex justify-end gap-2">
            <Button buttonText="Create" buttonRole="submit" />
          </div>
        </form>
      );
    }
    if (activeTab === 'endpointGroups') {
      return (
        <form className="grid gap-4" onSubmit={endpointGroupForm.handleSubmit}>
          <Input
            label="Name *"
            name="name"
            value={endpointGroupForm.values.name}
            onChange={endpointGroupForm.handleChange}
            onBlur={endpointGroupForm.handleBlur}
            onError={endpointGroupForm.touched.name ? endpointGroupForm.errors.name : null}
            isLabelIcon={true}
            iconTitle="The name of the VPN endpoint group."
          />
          <Input
            label="Description"
            name="description"
            value={endpointGroupForm.values.description}
            onChange={endpointGroupForm.handleChange}
            onBlur={endpointGroupForm.handleBlur}
            isLabelIcon={true}
            iconTitle="A description for the VPN endpoint group."
          />
          <SearchableSelect
            label="Endpoint Type *"
            name="endpoint_type"
            value={endpointGroupForm.values.endpoint_type}
            onChange={(e) => {
              endpointGroupForm.handleChange(e);
              endpointGroupForm.setFieldValue('endpointsText', '');
              endpointGroupForm.setFieldValue('subnetIds', []);
            }}
            onBlur={endpointGroupForm.handleBlur}
            options={enumOptions.endpoint_type}
            onError={endpointGroupForm.touched.endpoint_type ? endpointGroupForm.errors.endpoint_type : null}
            isLabelIcon={true}
            iconTitle="IPsec connection validation requires that local endpoints are subnets, and peer endpoints are CIDRs."
          />

          {endpointGroupForm.values.endpoint_type === 'subnet' ? (
            <div className="grid gap-2">
              <p className="text-sm font-semibold flex items-center">
                Local System Subnets
                <BsFillPatchQuestionFill
                  className="text-xs text-primaryBlue ml-1 cursor-pointer"
                  title="Select the local subnets for this endpoint group."
                />
              </p>
              <div className="grid gap-2 max-h-48 overflow-y-auto p-2 border rounded border-[#c4c4c432]">
                {(Array.isArray(subnetList) ? subnetList : []).map((s) => (
                  <label key={s.id} className="flex items-center gap-2 cursor-pointer text-sm">
                    <input
                      type="checkbox"
                      checked={endpointGroupForm.values.subnetIds.includes(s.id)}
                      onChange={(e) => {
                        const checked = e.target.checked;
                        const current = endpointGroupForm.values.subnetIds;
                        if (checked) {
                          endpointGroupForm.setFieldValue('subnetIds', [...current, s.id]);
                        } else {
                          endpointGroupForm.setFieldValue('subnetIds', current.filter((id) => id !== s.id));
                        }
                      }}
                    />
                    {s.cidr} {s.name ? `(${s.name})` : ''}
                  </label>
                ))}
              </div>
              {endpointGroupForm.touched.subnetIds && endpointGroupForm.errors.subnetIds && (
                <p className="text-red-500 text-xs">{endpointGroupForm.errors.subnetIds}</p>
              )}
            </div>
          ) : (
            <Input
              label="Endpoints (comma-separated) *"
              name="endpointsText"
              value={endpointGroupForm.values.endpointsText}
              onChange={endpointGroupForm.handleChange}
              onBlur={endpointGroupForm.handleBlur}
              onError={endpointGroupForm.touched.endpointsText ? endpointGroupForm.errors.endpointsText : null}
              isLabelIcon={true}
              iconTitle="Remote peer subnet(s) address(es) with mask(s) in CIDR format separated with commas if needed (e.g. 20.1.0.0/24, 21.1.0.0/24). This field is valid if type is CIDR."
            />
          )}
          <div className="flex justify-end gap-2">
            <Button buttonText="Create" buttonRole="submit" />
          </div>
        </form>
      );
    }
    const f = siteConnectionForm;
    const goNext = () => {
      const errs = validateSiteRequiredStep(f.values);
      if (Object.keys(errs).length) {
        f.setErrors(errs);
        touchFields(f, Object.keys(errs));
        setSiteFormStep('required');
        showErrorMessage('Please complete all required fields');
        return;
      }
      setSiteFormStep('optional');
    };

    return (
      <form className="grid gap-4" onSubmit={f.handleSubmit}>
        <div className="flex flex-wrap gap-3">
          <button
            type="button"
            onClick={() => setSiteFormStep('required')}
            className={`px-4 py-2 text-xs rounded-md border dark:border-[#c4c4c432] ${
              siteFormStep === 'required' ? 'bg-primaryBlue text-white' : 'bg-white dark:bg-slate-800'
            }`}
          >
            {editingSiteConnection ? 'Edit IPSec Site Connection *' : 'Add New IPSec Site Connection *'}
          </button>
          <button
            type="button"
            onClick={() => setSiteFormStep('optional')}
            className={`px-4 py-2 text-xs rounded-md border dark:border-[#c4c4c432] ${
              siteFormStep === 'optional' ? 'bg-primaryBlue text-white' : 'bg-white dark:bg-slate-800'
            }`}
          >
            Optional Parameters
          </button>
        </div>

        {siteFormStep === 'required' ? (
          <div className="grid md:grid-cols-2 gap-6">
            <div className="grid gap-4">
              <Input
                label="Name *"
                name="name"
                value={f.values.name}
                onChange={f.handleChange}
                onBlur={f.handleBlur}
                onError={f.touched.name ? f.errors.name : null}
                isLabelIcon={true}
                iconTitle="The name of the IPSec site connection."
              />
              <Input
                label="Description"
                name="description"
                value={f.values.description}
                onChange={f.handleChange}
                onBlur={f.handleBlur}
                onError={f.touched.description ? f.errors.description : null}
                isLabelIcon={true}
                iconTitle="A description for the IPSec site connection."
              />

              <SearchableSelect
                label="VPN service associated with this connection *"
                name="vpnservice_id"
                value={f.values.vpnservice_id} 
                disabled={!!editingSiteConnection}
                onChange={setSiteField('vpnservice_id')}
                onBlur={() => f.setFieldTouched('vpnservice_id', true)}
                options={serviceOptions}
                placeholder="VPN service name"
                  onError={siteError('vpnservice_id')}
              />

                            <SearchableSelect
                label="Endpoint group for local subnet(s) *"
                name="local_ep_group_id"
                value={f.values.local_ep_group_id}
                 disabled={!!editingSiteConnection}
                onChange={setSiteField('local_ep_group_id')}
                onBlur={() => f.setFieldTouched('local_ep_group_id', true)}
                options={localEndpointGroupOptions}
                placeholder="local endpoint group name"
                onError={siteError('local_ep_group_id')}
                isLabelIcon={true}
                iconTitle="Local subnets which the new IPsec connection is connected to. Required if no subnet is specified in a VPN service selected."
              />

              <SearchableSelect
                label="IKE policy associated with this connection *"
                name="ikepolicy_id"
                value={f.values.ikepolicy_id}
                 disabled={!!editingSiteConnection}
               onChange={setSiteField('ikepolicy_id')}
              onBlur={() => f.setFieldTouched('ikepolicy_id', true)}
              onError={siteError('ikepolicy_id')}
                options={ikeOptions}
                placeholder="IKE policy name"
              />

              <SearchableSelect
                label="IPSec policy associated with this connection *"
                name="ipsecpolicy_id"
                value={f.values.ipsecpolicy_id}
                disabled={!!editingSiteConnection}
                onChange={setSiteField('ipsecpolicy_id')}
                onBlur={() => f.setFieldTouched('ipsecpolicy_id', true)}
                options={ipsecOptions}
                placeholder="IPSec policy name"
                onError={siteError('ipsecpolicy_id')}
              />

              <Input
                label="Peer gateway public IPv4/IPv6 Address or FQDN *"
                name="peer_address"
                value={f.values.peer_address} 
                // disabled={!!editingSiteConnection}
                onChange={f.handleChange}
                onBlur={f.handleBlur}
                onError={f.touched.peer_address ? f.errors.peer_address : null}
                isLabelIcon={true}
                iconTitle="Peer gateway public IPv4/IPv6 Address or FQDN."
              />

              <Input
                label="Peer router identity for authentication (Peer ID) *"
                name="peer_id"
                value={f.values.peer_id}
                onChange={f.handleChange}
                onBlur={f.handleBlur}
                onError={f.touched.peer_id ? f.errors.peer_id : null}
                isLabelIcon={true}
                iconTitle="Peer router identity for authentication (Peer ID)."
              />

              <SearchableSelect
                label="Endpoint group for remote peer CIDR(s) *"
                name="peer_ep_group_id"
                value={f.values.peer_ep_group_id} 
                disabled={!!editingSiteConnection}
                onChange={setSiteField('peer_ep_group_id')}
                onBlur={() => f.setFieldTouched('peer_ep_group_id', true)}
                options={peerCidREndpointGroupOptions}
                placeholder="peer CIDR endpoint group name"
                onError={siteError('peer_ep_group_id')}
                isLabelIcon={true}
                iconTitle="Remote subnets which the new IPsec connection is connected to. Required if no peer CIDR is specified."
              />

              <Input
                label="Pre-Shared Key (PSK) string *"
                name="psk"
                type="password"
                isPassword="true"
                value={f.values.psk}
                // onChange={f.handleChange}
                // onBlur={f.handleBlur}
                onChange={setSiteField('psk')}
                onBlur={() => f.setFieldTouched('psk', true)}
                onError={f.touched.psk ? f.errors.psk : null}
                isLabelIcon={true}
                iconTitle="Pre-Shared Key (PSK) string."
              />

              <div className="flex justify-end gap-2">
                <button
                  type="button"
                  className="px-4 py-2 text-xs rounded-md cursor-pointer bg-primaryBlue text-white hover:opacity-90"
                  onClick={goNext}
                >
                  Next
                </button>
              </div>
            </div>

            <div className="hidden md:block">
              <p className="text-2xl font-semibold">Description:</p>
              <p className="text-sm opacity-80 mt-1">
                Create IPSec site connection for current project. Assign a name and description for
                the IPSec site connection. All fields in this tab are required.
              </p>
            </div>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 gap-6">
            <div className="grid gap-4">
              <Input
            label="Maximum Transmission Unit size for the connection"
            name="mtu"
            type="number"
            value={f.values.mtu}
            onChange={f.handleChange}
            onBlur={f.handleBlur}
            onError={siteError('mtu')}
            isLabelIcon={true}
            iconTitle="Maximum Transmission Unit size for the connection."
          />
              <SearchableSelect
                label="Dead peer detection actions"
                name="action"
                value={f.values.action}
                onChange={setSiteField('action')}
                onBlur={() => f.setFieldTouched('action', true)}
                options={enumOptions.dpd_action}
                onError={siteError('action')}
              />
              <Input
              label="Dead peer detection interval"
              name="interval"
              type="number"
              value={f.values.interval}
              onChange={f.handleChange}
              onBlur={f.handleBlur}
              onError={siteError('interval')}
              isLabelIcon={true}
            />
              <Input
            label="Dead peer detection timeout"
            name="timeout"
            type="number"
            value={f.values.timeout}
            onChange={f.handleChange}
            onBlur={f.handleBlur}
            onError={siteError('timeout')}
            isLabelIcon={true}
          />

              <SearchableSelect
                label="Initiator state"
                name="initiator"
                value={f.values.initiator}
                onChange={setSiteField('initiator')}
                onBlur={() => f.setFieldTouched('initiator', true)}
                options={enumOptions.initiator}
                onError={siteError('initiator')}
              />

              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  name="admin_state_up"
                  checked={!!f.values.admin_state_up}
                  onChange={f.handleChange}
                />
                <span className="text-sm flex items-center">
                  Enable Admin State
                  <BsFillPatchQuestionFill
                    className="text-sm text-primaryBlue ml-1 cursor-pointer"
                    title="The state of IPsec site connection to start in. If disabled (not checked), IPsec site connection does not forward packets."
                  />
                </span>
              </div>

                            {!editingSiteConnection && (
          <>
           <Select
              label="Payment Period *"
              name="payment"
              options={vpnPaymentOptions}
              valueKey="value"
              labelKey="label"
              selectValue={f.values.payment}
              selectBlur={() =>
                f.setFieldTouched('payment', true)
              }
              onChange={(e) => {
                const value = e.target.value;

                f.setFieldValue('payment', value);
                f.setFieldTouched('payment', true, false);

                if (f.errors.payment) {
                  f.setErrors({
                    ...f.errors,
                    payment: undefined,
                  });
                }
              }}
              onError={
                f.touched.payment
                  ? f.errors.payment
                  : null
              }
            />
         

            {f.values.payment && (
              <div className="rounded-md border border-blue-200 bg-blue-50 p-4 dark:border-blue-900 dark:bg-blue-900/20">
                <div className="flex items-center justify-between gap-4">
                  <div>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      VPN Subscription
                    </p>

                    <p className="mt-1 text-sm font-medium">
                      {f.values.payment === 'monthly'
                        ? 'Monthly'
                        : 'Yearly'}
                    </p>
                  </div>

                 <p className="text-lg font-semibold text-primaryBlue">
                {vpnPriceLoading
                  ? 'Loading...'
                  : getVpnMonthlyPrice() !== null
                    ? `${
                        currency === 'NGN' ? '₦' : '$'
                      }${calculateVpnPrice(
                        f.values.payment
                      ).toLocaleString()}`
                    : 'Price unavailable'}
              </p>
                </div>
              </div>
            )}
          </>
        )}

              <div className="flex justify-end gap-2">
                <button
                  type="button"
                  className="px-4 py-2 text-xs border-[1.5px] border-[#c4c4c460] rounded-md cursor-pointer bg-white dark:bg-slate-800"
                  onClick={() => setSiteFormStep('required')}
                >
                  Back
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 text-xs rounded-md cursor-pointer bg-primaryBlue text-white hover:opacity-90"
                >
                  {editingSiteConnection ? 'Save Changes' : 'Add'}
                </button>
              </div>
            </div>

            <div className="hidden md:block">
              <p className="text-2xl font-semibold">Description:</p>
              <p className="text-sm opacity-80 mt-1">
                Fields in this tab are optional. You can configure the detail of IPSec site
                connection created.
              </p>
            </div>
          </div>
        )}
      </form>
    );
  };

  return (
    <div className="px-4 md:px-10 py-6 min-h-screen">
      <Spinner loading={loading} />

      <p className="crimson-font text-xl font-medium text-primaryBlue mb-4">
        Virtual Private Network
      </p>

      <div className="flex flex-wrap gap-3 mb-8">
        {tabs.map((t) => (
          <button
            key={t.key}
            onClick={() => setActiveTab(t.key)}
            className={`px-5 py-3 rounded-md text-sm border dark:border-[#c4c4c432] ${
              activeTab === t.key
                ? 'bg-white dark:bg-slate-800 text-primaryBlue border-primaryBlue'
                : 'bg-transparent text-primaryBlue'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div className="grid md:flex md:items-center md:justify-between gap-4 mb-4">
        <div className="flex gap-2 items-center w-full md:w-auto">
          <select
            value={filterKey}
            onChange={(e) => setFilterKey(e.target.value)}
            className="bg-white text-black p-3 text-sm rounded-md border border-[#c4c4c460] dark:bg-slate-800 dark:text-white"
          >
            <option value="name">Name =</option>
            {(activeTab === 'ike' || activeTab === 'ipsec') && (
              <>
                <option value="auth">Authorization algorithm =</option>
                <option value="encryption">Encryption algorithm =</option>
                <option value="pfs">PFS =</option>
              </>
            )}
            {activeTab === 'services' && (
              <>
                <option value="subnet_id">Subnet ID =</option>
                <option value="subnet">Subnet =</option>
                <option value="router_id">Router ID =</option>
                <option value="router">Router =</option>
              </>
            )}
            {activeTab === 'endpointGroups' && (
              <>
                <option value="type">Type =</option>
                <option value="endpoints">Endpoints =</option>
              </>
            )}
            {activeTab === 'siteConnections' && (
              <>
                <option value="vpnservice">VPN Service =</option>
                <option value="vpnservice_id">VPN Service ID =</option>
                <option value="ikepolicy">IKE Policy =</option>
                <option value="ikepolicy_id">IKE Policy ID =</option>
                <option value="ipsecpolicy">IPSec Policy =</option>
                <option value="ipsecpolicy_id">IPSec Policy ID =</option>
                <option value="status">Status =</option>
              </>
            )}
          </select>
          <input
            type="search"
            value={filterText}
            onChange={(e) => setFilterText(e.target.value)}
            placeholder="Search..."
            className="bg-white text-black w-full md:w-72 p-3 text-sm rounded-md border border-[#c4c4c460] active:outline-0 focus:outline-0 placeholder:opacity-70 placeholder:text-xs dark:bg-slate-800 dark:text-white"
          />
          <button
            className="bg-white dark:bg-slate-800 border border-[#c4c4c460] rounded-md py-3 px-5 text-sm"
            onClick={() => {}}
            type="button"
          >
            Filter
          </button>
        </div>

        <div className="flex gap-2 justify-end">
          <button
            onClick={openCreate}
            className="px-4 py-3 text-xs border-[1.5px] border-primaryBlue rounded-md text-primaryBlue cursor-pointer w-fit bg-white dark:bg-slate-800"
          >
            + {createTitle}
          </button>
          <button
            onClick={openBulkDelete}
            disabled={selectedIds.size === 0}
            className={`px-4 py-3 text-xs rounded-md cursor-pointer w-fit ${
              selectedIds.size === 0
                ? 'opacity-50 text-white bg-red-300'
                : 'text-white bg-red-500 hover:bg-red-600'
            }`}
          >
            Delete {tabs.find((t) => t.key === activeTab)?.label}
          </button>
        </div>
      </div>

      <div className="text-xs opacity-70 mb-3">Displaying {filtered.length} item</div>

      <div className="overflow-x-auto rounded-md dark:text-white">{renderTable()}</div>

            <Modal 
        title={createTitle} 
        isVisible={createOpen} 
        onClose={() => setCreateOpen(false)} 
        size={
          activeTab === 'siteConnections'
            ? "md:w-[85%] md:max-h-[95%] overflow-visible"
            : "md:w-[55%] overflow-visible"
        }
      >
        {renderCreateForm()}
      </Modal>

      <Modal
  title="Edit IPSec Site Connection"
  isVisible={editSiteConnectionOpen}
  onClose={() => {
    setEditSiteConnectionOpen(false);
    setEditingSiteConnection(null);
    siteConnectionForm.resetForm();
  }}
  size="md:w-[85%] md:max-h-[95%] overflow-visible"
>
  {renderCreateForm()}
</Modal>

    <PaymentModal
      isOpen={showPaymentModal}
      onClose={() => {
      setShowPaymentModal(false);

      // Return user to the IPSec form with their existing values preserved
      setCreateOpen(true);
    }}
      currency={currency}
      totalPrice={calculateVpnPrice(
        siteConnectionForm.values.payment
      )}
      walletBalance={walletBalance}
      virtualAccount={virtualAccount}
      onPaymentSuccess={() => {
        setShowPaymentModal(false);
        setPaymentVerified(true);

        // Refresh wallet after payment if needed
        // dispatch(getUserVirtualAccount());

        setTimeout(() => {
          siteConnectionForm.handleSubmit();
        }, 0);
      }}
    />

      <ConfirmDeleteModal
        open={confirmDelete.open}
        name={confirmDelete.name}
        title={confirmDelete.bulk ? 'Delete selected items' : (confirmDelete.title || 'Delete item')}
       description={confirmDelete.bulk ? `Type "Delete" to confirm deletion of ${selectedIds.size} item${selectedIds.size > 1 ? 's' : ''}`
      : 'Type the name exactly to confirm deletion'} 
      onClose={() => setConfirmDelete({ open: false, id: '', name: '', title: '', bulk: false })}
        onConfirm={runDelete}
        isDeleting={loading}
      />
    </div>
  );
};

export default VPN;


